import { useRef, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import type { WakeCommand } from '@/workers/wakeword-grammar';
import { useRecordingSessionStore } from '@/stores/useRecordingSessionStore';
import { resolveNextAppointment } from '@/utils/nextAppointment';

/**
 * Wake Word Detection Hook
 *
 * Provides hands-free activation via "Hey MAIE" wake word detection.
 * Supports two modes:
 * 1. Client-side Whisper: Fast local transcription (~1s, 85-90% accuracy)
 * 2. Backend Whisper: High-quality server transcription (slower, 95%+ accuracy)
 *
 * Features:
 * - Voice Activity Detection (VAD) for automatic stop after silence
 * - Prevents re-triggering while already active (like "Hey Siri")
 * - Audio feedback (beeps) for start/stop
 * - Optional client-side transcription preview
 * - Verb grammar: "hey MAIE start encounter / pause / stop / resume"
 *   recognized in a single utterance and dispatched via onWakeCommand.
 *   See workers/wakeword-grammar.ts for the full phrase list.
 */

interface WakeWordConfig {
  enabled: boolean;
  useClientSideWhisper: boolean;
  mode: 'always-listening' | 'push-to-activate';
  pushToActivateKey: string;
  onWakeWordDetected: () => void; // Bare wake word: opens chat bubble and starts recording.
  onTranscriptionReady: (text: string) => void; // Callback with transcript (client-side mode only)
  /**
   * Wake word + recognized verb. The hook applies the state policy
   * itself (suppress chat open during active recording, ignore
   * `start-encounter` if a session is already capturing/paused, etc.)
   * before invoking this; consumers should treat the call as "do the
   * thing" without re-checking state.
   */
  onWakeCommand?: (command: WakeCommand) => void;
}

interface WakeWordState {
  isActive: boolean; // Currently listening (prevents re-trigger)
  isListening: boolean; // Model loaded and ready
  isManuallyActivated: boolean; // Manually activated via push-to-activate
  status: string;
}

export const useWakeWordDetection = (config: WakeWordConfig) => {
  const workerRef = useRef<Worker | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const isProcessingActiveRef = useRef(false);
  const isActiveSessionRef = useRef(false); // Prevents re-triggering during active session
  const lastDetectionRef = useRef<{ text: string; timestamp: number } | null>(null);

  const stateRef = useRef<WakeWordState>({
    isActive: false,
    isListening: false,
    isManuallyActivated: false,
    status: 'initializing'
  });

  // First-time model download UI state. Lives in React state (not refs) so
  // consumers re-render as the worker streams progress events.
  const [isModelLoading, setIsModelLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState<number | null>(null);
  // Mirror of stateRef.isListening as React state. The ref is the source of
  // truth (worker writes to it from raw onmessage), but consumers like the
  // wake-word onboarding modal need to *react* to "listening just became
  // true" — which a ref read alone never triggers, especially in the
  // cache-hit case where setIsModelLoading(false) is a no-op and no other
  // setState fires after the worker reports ready.
  const [isListeningPublic, setIsListeningPublic] = useState(false);

  const pushToActivateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Live mirror of `config.mode`. The main worker effect captures `config`
  // in closure when it first runs (deps are `[config.enabled]`, not mode),
  // so without a ref the 'ready' handler would auto-start in whatever mode
  // was active at mount time and never react to a mid-session radio flip.
  const modeRef = useRef(config.mode);
  useEffect(() => {
    modeRef.current = config.mode;
  }, [config.mode]);

  // Play beep sound
  const playBeep = useCallback((frequency: number = 800, duration: number = 0.2) => {
    try {
      const audioContext = new AudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    } catch (error) {
      console.error('[WakeWord] Failed to play beep:', error);
    }
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    console.log('[WakeWord] Cleaning up...');

    isProcessingActiveRef.current = false;

    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    stateRef.current.isListening = false;
    setIsListeningPublic(false);
  }, []);

  const stopListening = useCallback(() => {
    cleanup();
  }, [cleanup]);

  // Manual activation for push-to-activate mode
  const activate = useCallback(() => {
    console.log('[WakeWord] Manual activation triggered');

    // Clear any existing timeout
    if (pushToActivateTimeoutRef.current) {
      clearTimeout(pushToActivateTimeoutRef.current);
    }

    stateRef.current.isManuallyActivated = true;
    playBeep(1200, 0.1); // High beep to indicate activation

    //  Note: startListening is defined later in the file, which is fine since this is only called after model loads

    // Auto-deactivate after 10 seconds
    pushToActivateTimeoutRef.current = setTimeout(() => {
      console.log('[WakeWord] Push-to-activate timeout - stopping listening');
      stateRef.current.isManuallyActivated = false;
      playBeep(800, 0.1); // Lower beep to indicate timeout
      stopListening();
    }, 10000); // 10 seconds
  }, [playBeep, stopListening]);

  // Initialize wake word detection
   
  useEffect(() => {
    if (!config.enabled) return;

    console.log('[WakeWord] Initializing wake word detection...');

    try {
      // Create Web Worker for voice recognition
      const worker = new Worker(
        new URL('../workers/wakeword-worker.ts', import.meta.url),
        { type: 'module' }
      );

      worker.onerror = (error) => {
        // ErrorEvent carries `message` / `filename` / `lineno` for in-worker
        // throws; bare `Event` (eventPhase=2, no fields) means the worker's
        // module graph failed to load before any of its own code ran. Log
        // both shapes so production reports are debuggable.
        const ev = error as ErrorEvent;
        console.error(
          '[WakeWord] Worker error:',
          ev.message || '(no message — module-graph load failure)',
          ev.filename ? `at ${ev.filename}:${ev.lineno}:${ev.colno}` : '',
          error,
        );
        stateRef.current.status = 'error';
      };

      worker.onmessageerror = (event) => {
        console.error('[WakeWord] Worker messageerror:', event);
        stateRef.current.status = 'error';
      };

      worker.onmessage = (event) => {
        const { status, text, detected, audio, duration, message, command, progress } =
          event.data;

        if (status === 'loading') {
          // Worker posts this BEFORE checking the browser cache. On cache
          // hits no `progress` events follow and `ready` arrives in the
          // same tick — so we deliberately do NOT show the download chip
          // here. The chip is gated on real `progress` events below,
          // which transformers.js only emits during actual network
          // downloads. Without this gate the chip would flash on every
          // page refresh of an already-cached browser.
          stateRef.current.status = 'loading';
        } else if (status === 'progress') {
          stateRef.current.status = 'loading';
          setIsModelLoading(true);
          if (typeof progress === 'number') {
            // Force monotonic — the worker already aggregates across files,
            // but if a `total` estimate slips we don't want the bar to
            // visibly retreat. UX-wise people read "going down" as broken.
            setLoadingProgress((prev) =>
              prev === null ? progress : Math.max(prev, progress),
            );
          }
        } else if (status === 'ready') {
          stateRef.current.status = 'ready';
          stateRef.current.isListening = true;
          setIsModelLoading(false);
          setLoadingProgress(null);
          setIsListeningPublic(true);
          console.log('[WakeWord] Model loaded, ready to listen');
          // Don't decide here whether to startListening — `setIsListeningPublic(true)`
          // above will trigger the mode-change effect, which is the
          // single source of truth for "should the mic loop be running
          // right now?" and avoids the ready-handler-vs-mode-effect race
          // that would otherwise call startListening() twice in always-
          // listening mode.
          if (modeRef.current === 'push-to-activate') {
            console.log('[WakeWord] Push-to-activate mode - waiting for manual activation');
          }
        } else if (status === 'wake-word-command') {
          // Wake + recognized verb. Policy + dispatch happen in
          // handleWakeCommand; we DON'T fall through to the chat-open path.
          // Logging the raw transcript matters: command-path utterances
          // never hit the `transcription` branch below, so without this
          // line a false positive shows up as a mystery dispatch with no
          // way to see what Whisper actually heard.
          console.log(
            '[WakeWord] ⚡ Wake + command:',
            command,
            '| transcript:',
            JSON.stringify(text ?? '')
          );
          handleWakeCommand(command as WakeCommand);
        } else if (status === 'wake-word-detected') {
          // Bare wake word — open chat. Suppressed during active recording
          // so a passing "hey MAIE" mid-consult doesn't pull up the panel.
          const recState = useRecordingSessionStore.getState().session?.status;
          if (recState === 'capturing' || recState === 'paused') {
            console.log(
              '[WakeWord] bare wake suppressed — recording is',
              recState
            );
            return;
          }
          console.log(
            '[WakeWord] ⚡ Immediate wake word detection! transcript:',
            JSON.stringify(text ?? '')
          );

          if (isActiveSessionRef.current) {
            console.log('[WakeWord] Already in active session - ignoring');
            return;
          }

          handleWakeWordDetection(text, audio, duration);
        } else if (status === 'transcription') {
          if (text && text.trim()) {
            console.log('[WakeWord] Transcript:', text);

            // Check if wake word detected AND not already in active session
            if (detected && !isActiveSessionRef.current) {
              handleWakeWordDetection(text, audio, duration);
            }
          }
        } else if (status === 'error') {
          console.error('[WakeWord] Transcription error:', message ?? '(no message)');
          stateRef.current.status = 'error';
          setIsModelLoading(false);
          setLoadingProgress(null);
        }
      };

      // Load model
      worker.postMessage({ type: 'load' });
      workerRef.current = worker;

      return () => {
        worker.terminate();
        cleanup();
      };
    } catch (error) {
      console.error('[WakeWord] Failed to initialize:', error);
    }
  }, [config.enabled]);

  // React to mode flips after the model is already ready. The main
  // worker effect only does the start/stop decision once (when the
  // worker first reports 'ready'); subsequent radio-button changes
  // would otherwise be silent — the mic loop wouldn't start when the
  // user picks "always-listening", and wouldn't stop when they go
  // back to push-to-activate.

  useEffect(() => {
    if (!config.enabled) return;
    if (!isListeningPublic) return; // model not ready yet — first 'ready' handler will pick it up

    if (config.mode === 'always-listening' && !isProcessingActiveRef.current) {
      console.log('[WakeWord] Mode flipped to always-listening — starting mic loop');
      startListening();
    } else if (
      config.mode === 'push-to-activate' &&
      isProcessingActiveRef.current &&
      !stateRef.current.isManuallyActivated
    ) {
      console.log('[WakeWord] Mode flipped to push-to-activate — stopping mic loop');
      cleanup();
    }
  }, [config.enabled, config.mode, isListeningPublic]);

  // Keyboard shortcut listener for push-to-activate mode
   
  useEffect(() => {
    if (!config.enabled || config.mode !== 'push-to-activate') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Parse the shortcut (e.g., "ctrl+space")
      const parts = config.pushToActivateKey.toLowerCase().split('+');
      const key = parts[parts.length - 1];
      const requiresCtrl = parts.includes('ctrl');
      const requiresAlt = parts.includes('alt');
      const requiresShift = parts.includes('shift');

      // Check if the key matches
      const keyMatches = e.key.toLowerCase() === key ||
        (key === 'space' && e.code === 'Space');
      const ctrlMatches = !requiresCtrl || e.ctrlKey;
      const altMatches = !requiresAlt || e.altKey;
      const shiftMatches = !requiresShift || e.shiftKey;

      if (keyMatches && ctrlMatches && altMatches && shiftMatches) {
        e.preventDefault();
        console.log('[WakeWord] Keyboard shortcut triggered:', config.pushToActivateKey);
        activate();

        // Start listening if model is ready but not yet listening
        if (stateRef.current.isListening && !isProcessingActiveRef.current) {
          startListening();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    console.log('[WakeWord] Keyboard listener registered:', config.pushToActivateKey);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      console.log('[WakeWord] Keyboard listener removed');
    };
  }, [config.enabled, config.mode, config.pushToActivateKey, activate]);

  /**
   * Apply the recording-state policy to a recognized wake-word command
   * and dispatch into the recording session store. Reads store state via
   * `getState()` (no subscription, no re-render churn). See
   * docs/LAUNCHER_PILL_SPEC.md for the full state table.
   *
   * Behavior matrix:
   *   Idle      → start-encounter dispatches; pause/resume/stop ignored.
   *   Capturing → pause / stop dispatch; confirm-stop shows toast;
   *               start-encounter / resume ignored.
   *   Paused    → resume / stop dispatch; confirm-stop shows toast;
   *               start-encounter / pause ignored.
   */
  const handleWakeCommand = useCallback((command: WakeCommand) => {
    const { session, startSession, pause, resume, stop } =
      useRecordingSessionStore.getState();

    const isCapturing = !!session && session.status === 'capturing';
    const isPaused = !!session && session.status === 'paused';
    const isActive = isCapturing || isPaused;

    // Tell the consumer regardless — useful for UI feedback / telemetry.
    config.onWakeCommand?.(command);

    if (command === 'start-encounter') {
      if (isActive) {
        console.log('[WakeWord] start-encounter ignored: session already active');
        return;
      }
      console.log('[WakeWord] dispatching startSession({}) from voice command');
      void startSession({});
      toast.success('Encounter started by voice');
      return;
    }

    if (command === 'start-next') {
      if (isActive) {
        console.log('[WakeWord] start-next ignored: session already active');
        toast('Already recording — finish the current encounter first.');
        return;
      }
      console.log('[WakeWord] resolving next scheduled appointment for voice start-next');
      void (async () => {
        try {
          const next = await resolveNextAppointment();
          if (!next) {
            toast('No upcoming appointment to start.');
            return;
          }
          // Re-check the live session right before dispatching: the
          // resolver may have hit the network, so by the time we
          // started, the doctor (or another tab) could have launched
          // a session manually. The store guards against double-start
          // anyway (`if (get().session) return get().session;`), but
          // the toast shouldn't lie.
          if (useRecordingSessionStore.getState().session) {
            console.log('[WakeWord] start-next aborted: session began during resolve');
            return;
          }
          await startSession({
            patientId: next.patientId,
            patientNameEn: next.patientNameEn,
            patientNameAr: next.patientNameAr,
          });
          toast.success(
            next.patientNameEn
              ? `Encounter started — ${next.patientNameEn}`
              : 'Next encounter started by voice',
          );
        } catch (err) {
          console.error('[WakeWord] start-next failed:', err);
          toast.error("Couldn't start the next encounter.");
        }
      })();
      return;
    }

    if (command === 'pause') {
      if (!isCapturing) {
        console.log('[WakeWord] pause ignored: no active capture');
        return;
      }
      console.log('[WakeWord] dispatching pause() from voice command');
      void pause();
      toast('Recording paused');
      return;
    }

    if (command === 'resume') {
      if (!isPaused) {
        console.log('[WakeWord] resume ignored: not paused');
        return;
      }
      console.log('[WakeWord] dispatching resume() from voice command');
      void resume();
      toast('Recording resumed');
      return;
    }

    if (command === 'stop') {
      if (!isActive) {
        console.log('[WakeWord] stop ignored: no active session');
        return;
      }
      console.log('[WakeWord] dispatching stop() from voice command');
      void stop();
      toast.success('Recording stopped');
      return;
    }

    if (command === 'confirm-stop') {
      if (!isActive) {
        console.log('[WakeWord] confirm-stop ignored: no active session');
        return;
      }
      // Soft closure — surface a non-blocking toast with action buttons
      // so the doctor can confirm or wave it off without the recording
      // pausing. Auto-dismiss after 12s; default action keeps recording.
      console.log('[WakeWord] confirm-stop → prompting');
      toast('Are you done with this encounter?', {
        description: 'I heard a closing phrase. Stop the recording?',
        duration: 12_000,
        action: {
          label: 'Yes, stop',
          onClick: () => {
            console.log('[WakeWord] confirm-stop confirmed → stop()');
            void stop();
          },
        },
        cancel: {
          label: 'Keep going',
          onClick: () => {
            console.log('[WakeWord] confirm-stop cancelled');
          },
        },
      });
      return;
    }
  }, [config]);

  const handleWakeWordDetection = useCallback((text: string, _audio: Float32Array, _duration: number) => {
    const now = Date.now();
    const last = lastDetectionRef.current;

    // Deduplicate: ignore if similar text within 3 seconds
    const getWords = (s: string) => s.toLowerCase().split(/\s+/).filter(w => w.length > 2);
    const textWords = new Set(getWords(text));
    const lastWords = last ? new Set(getWords(last.text)) : new Set();

    const intersection = new Set([...textWords].filter(w => lastWords.has(w)));
    const similarity = lastWords.size > 0 ? intersection.size / lastWords.size : 0;

    const isDuplicate = last &&
      (now - last.timestamp < 3000) &&
      (similarity > 0.6 || text === last.text);

    if (isDuplicate) {
      console.log('[WakeWord] Duplicate ignored:', text, `(${Math.round(similarity * 100)}% similar)`);
      return;
    }

    // Store this detection
    lastDetectionRef.current = { text, timestamp: now };

    console.log('🎯 WAKE WORD DETECTED:', text);

    // Play activation beep (high tone)
    playBeep(1000, 0.15);

    // Mark as active session (prevents re-triggering until session ends)
    isActiveSessionRef.current = true;
    stateRef.current.isActive = true;

    // Trigger chat bubble to open
    config.onWakeWordDetected();

    // Always use backend recording for the actual command
    // Client-side Whisper is ONLY for fast wake word detection
    console.log('[WakeWord] Wake word detected - starting backend recording for full command');

    // The onWakeWordDetected callback will trigger startVoiceRecording()
    // which will record and transcribe the full command via backend

    // Reset active session after delay to allow recording to complete
    setTimeout(() => {
      isActiveSessionRef.current = false;
      stateRef.current.isActive = false;
    }, 5000); // Give time for user to complete their command
  }, [config, playBeep]);

  const startListening = useCallback(async () => {
    if (isProcessingActiveRef.current) {
      console.log('[WakeWord] Already listening');
      return;
    }

    try {
      console.log('[WakeWord] Starting to listen...');

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      // Create audio context with hardware default sample rate
      audioContextRef.current = new AudioContext();
      const actualSampleRate = audioContextRef.current.sampleRate;
      console.log(`[WakeWord] Sample rate: ${actualSampleRate} Hz`);

      const source = audioContextRef.current.createMediaStreamSource(stream);
      sourceNodeRef.current = source;

      // Create processor for continuous chunks
      const bufferSize = 4096;
      processorRef.current = audioContextRef.current.createScriptProcessor(bufferSize, 1, 1);

      isProcessingActiveRef.current = true;

      const TARGET_SAMPLE_RATE = 16000;

      // VAD parameters
      let utteranceBuffer: number[] = [];
      let isSpeaking = false;
      let silenceFrames = 0;
      const SILENCE_THRESHOLD = 0.01;
      // Each frame is bufferSize/sampleRate seconds (e.g. 4096/48000 ≈ 85 ms).
      // 3 frames ≈ 250 ms of trailing silence — the prior 6 frames ≈ 510 ms
      // felt sluggish for "Hey MAIE, stop" because the doctor finishes
      // speaking and waits half a second before anything happens. 250 ms
      // is past natural intra-word pauses (~80–150 ms) without bleeding into
      // the next utterance.
      const SILENCE_DURATION_FRAMES = 3;
      const MIN_UTTERANCE_LENGTH = actualSampleRate * 0.1; // 0.1s minimum to capture short phrases like "number two"
      const MAX_UTTERANCE_LENGTH = actualSampleRate * 5;

      const resample = (sourceData: Float32Array, sourceRate: number, targetRate: number): Float32Array => {
        if (Math.abs(sourceRate - targetRate) < 100) {
          return sourceData;
        }

        const ratio = sourceRate / targetRate;
        const targetLength = Math.round(sourceData.length / ratio);
        const result = new Float32Array(targetLength);

        for (let i = 0; i < targetLength; i++) {
          const sourceIndex = i * ratio;
          const index = Math.floor(sourceIndex);
          const fraction = sourceIndex - index;

          if (index + 1 < sourceData.length) {
            result[i] = sourceData[index] * (1 - fraction) + sourceData[index + 1] * fraction;
          } else if (index < sourceData.length) {
            result[i] = sourceData[index];
          } else {
            result[i] = 0;
          }
        }

        return result;
      };

      const calculateRMS = (data: Float32Array): number => {
        let sumSquares = 0;
        for (let i = 0; i < data.length; i++) {
          sumSquares += data[i] * data[i];
        }
        return Math.sqrt(sumSquares / data.length);
      };

      processorRef.current.onaudioprocess = (e) => {
        if (!isProcessingActiveRef.current) return;

        const inputData = e.inputBuffer.getChannelData(0);
        const rms = calculateRMS(inputData);

        // Voice Activity Detection
        if (rms > SILENCE_THRESHOLD) {
          if (!isSpeaking) {
            isSpeaking = true;
            utteranceBuffer = [];
          }
          silenceFrames = 0;
          utteranceBuffer.push(...Array.from(inputData));

          if (utteranceBuffer.length > MAX_UTTERANCE_LENGTH) {
            processUtterance();
          }
        } else {
          if (isSpeaking) {
            utteranceBuffer.push(...Array.from(inputData));
            silenceFrames++;

            if (silenceFrames >= SILENCE_DURATION_FRAMES) {
              processUtterance();
            }
          }
        }

        function processUtterance() {
          if (utteranceBuffer.length < MIN_UTTERANCE_LENGTH) {
            isSpeaking = false;
            silenceFrames = 0;
            utteranceBuffer = [];
            return;
          }

          const sourceAudio = new Float32Array(utteranceBuffer);
          let resampledAudio = resample(sourceAudio, actualSampleRate, TARGET_SAMPLE_RATE);

          // Normalize audio
          let maxAbs = 0;
          for (let i = 0; i < resampledAudio.length; i++) {
            const abs = Math.abs(resampledAudio[i]);
            if (abs > maxAbs) maxAbs = abs;
          }
          if (maxAbs > 0 && maxAbs < 1.0) {
            const normalizedAudio = new Float32Array(resampledAudio.length);
            const scaleFactor = 0.95 / maxAbs;
            for (let i = 0; i < resampledAudio.length; i++) {
              normalizedAudio[i] = resampledAudio[i] * scaleFactor;
            }
            resampledAudio = normalizedAudio;
          }

          // Send to worker for wake word detection
          workerRef.current?.postMessage({
            type: 'transcribe',
            audio: resampledAudio
          });

          // Reset
          isSpeaking = false;
          silenceFrames = 0;
          utteranceBuffer = [];
        }
      };

      source.connect(processorRef.current);
      processorRef.current.connect(audioContextRef.current.destination);

      stateRef.current.isListening = true;
      console.log('[WakeWord] Listening for wake word...');

    } catch (error) {
      console.error('[WakeWord] Failed to start listening:', error);
      isProcessingActiveRef.current = false;
    }
  }, []);

  const stop = useCallback(() => {
    cleanup();
  }, [cleanup]);

  return {
    isActive: stateRef.current.isActive,
    isListening: isListeningPublic,
    isManuallyActivated: stateRef.current.isManuallyActivated,
    status: stateRef.current.status,
    isModelLoading,
    loadingProgress,
    stop,
    activate
  };
};
